import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

/**
 * Debug Context
 * Manages debug mode state and log collection for on-screen debugging
 */

export interface DebugLogEntry {
  id: number;
  timestamp: string;
  level: 'debug' | 'info' | 'warn' | 'error';
  message: string;
  meta?: object;
}

interface DebugContextType {
  isDebugMode: boolean;
  toggleDebugMode: () => void;
  setDebugMode: (enabled: boolean) => void;
  logs: DebugLogEntry[];
  addLog: (level: DebugLogEntry['level'], message: string, meta?: object) => void;
  clearLogs: () => void;
}

const DebugContext = createContext<DebugContextType | undefined>(undefined);

const DEBUG_MODE_KEY = 'pacepilot_debug_mode';
const MAX_LOGS = 500; // Keep last 500 logs to prevent memory issues

let logIdCounter = 0;

// Global log buffer for logs that happen before context is mounted
const pendingLogs: DebugLogEntry[] = [];
let contextMounted = false;
let addLogCallback:
  | ((level: DebugLogEntry['level'], message: string, meta?: object) => void)
  | null = null;

/**
 * Global function to add logs from anywhere (including logger.ts)
 * This is called by the logger before React context is available
 */
export function pushDebugLog(level: DebugLogEntry['level'], message: string, meta?: object): void {
  const entry: DebugLogEntry = {
    id: ++logIdCounter,
    timestamp: new Date().toISOString(),
    level,
    message,
    meta,
  };

  if (contextMounted && addLogCallback) {
    addLogCallback(level, message, meta);
  } else {
    // Buffer logs until context is ready
    pendingLogs.push(entry);
    if (pendingLogs.length > MAX_LOGS) {
      pendingLogs.shift();
    }
  }
}

interface DebugProviderProps {
  children: ReactNode;
}

export const DebugProvider: React.FC<DebugProviderProps> = ({ children }) => {
  const [isDebugMode, setIsDebugModeState] = useState<boolean>(() => {
    try {
      const stored = localStorage.getItem(DEBUG_MODE_KEY);
      return stored === 'true';
    } catch {
      return false;
    }
  });

  const [logs, setLogs] = useState<DebugLogEntry[]>([]);

  const addLog = useCallback((level: DebugLogEntry['level'], message: string, meta?: object) => {
    const entry: DebugLogEntry = {
      id: ++logIdCounter,
      timestamp: new Date().toISOString(),
      level,
      message,
      meta,
    };

    setLogs(prevLogs => {
      const newLogs = [...prevLogs, entry];
      // Keep only the last MAX_LOGS entries
      if (newLogs.length > MAX_LOGS) {
        return newLogs.slice(-MAX_LOGS);
      }
      return newLogs;
    });
  }, []);

  const clearLogs = useCallback(() => {
    setLogs([]);
  }, []);

  const setDebugMode = useCallback((enabled: boolean) => {
    setIsDebugModeState(enabled);
    try {
      localStorage.setItem(DEBUG_MODE_KEY, String(enabled));
    } catch {
      // Ignore localStorage errors
    }
  }, []);

  const toggleDebugMode = useCallback(() => {
    setDebugMode(!isDebugMode);
  }, [isDebugMode, setDebugMode]);

  // Register the callback and process pending logs when context mounts
  useEffect(() => {
    contextMounted = true;
    addLogCallback = addLog;

    // Process any pending logs
    if (pendingLogs.length > 0) {
      setLogs(prev => [...prev, ...pendingLogs].slice(-MAX_LOGS));
      pendingLogs.length = 0;
    }

    return () => {
      contextMounted = false;
      addLogCallback = null;
    };
  }, [addLog]);

  return (
    <DebugContext.Provider
      value={{
        isDebugMode,
        toggleDebugMode,
        setDebugMode,
        logs,
        addLog,
        clearLogs,
      }}
    >
      {children}
    </DebugContext.Provider>
  );
};

export const useDebug = (): DebugContextType => {
  const context = useContext(DebugContext);
  if (!context) {
    throw new Error('useDebug must be used within a DebugProvider');
  }
  return context;
};
